import React, { useRef, useState } from 'react';
import { Box, Button, TextField } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import type { FormikProps } from 'formik';
import * as Yup from 'yup';
import { passwordRegexConstant } from '../../constants';
import { changePasswordApi } from '../../apis';
import { AlertType, APIResponse, FreeKeyObject, User } from '../../utilities/types';
import { getApiErrorMessage } from '../../utilities/helper';
import CustomAlert from '../elements/custom-alert.element';
import { changePasswordUserApi } from '../../apis/admin';

// ✅ Validation Schema
const validationSchema = Yup.object().shape({
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required')
    .matches(
      passwordRegexConstant,
      'Password must contain at least one letter, one number and one special character'
    ),
  confirm_password: Yup.string()
    .oneOf([Yup.ref('password'), undefined], 'Passwords must match')
    .required('Confirm Password is required'),
});

interface FormProps {
  user: User;
  onSuccess?: () => void;
}

const UserChangePasswordForm: React.FC<FormProps> = ({ user, onSuccess }) => {
  const formikRef = useRef<FormikProps<{
    password: string;
    confirm_password: string;
  }> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [alertMessage, showAlertMessage] = useState<{ message: string; type: AlertType }>({
    message: '',
    type: 'info',
  });

  const toggleAlertMessage = (message: string, type: AlertType) => {
    if (message === '') {
      return;
    }
    showAlertMessage({ message, type });
    setTimeout(() => {
      showAlertMessage({ message: '', type: 'info' });
    }, 4000);
  };

  const onsubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!formikRef.current) {
      return;
    }
    const { values } = formikRef.current;
    setIsSubmitting(true);
    try {
      const payload = {
        user_id: user.id,
        password: values.password,
      };
      const response = (await changePasswordUserApi(payload)) as APIResponse<FreeKeyObject>;
      if (response.status) {
        formikRef.current.resetForm();
        onSuccess?.();
      }
    } catch (err: any) {
      toggleAlertMessage(getApiErrorMessage(err), 'error');
    }
    setIsSubmitting(false);
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        password: '',
        confirm_password: '',
      }}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={(values) => {
        console.log('Form Submitted:', values);
      }}
    >
      {({ errors, touched, isValid, dirty, handleChange }) => (
        <Form>
          <Field
            as={TextField}
            name="password"
            label="Password *"
            type="password"
            placeholder="Enter your password"
            fullWidth
            margin="normal"
            onChange={handleChange}
            error={touched.password && Boolean(errors.password)}
            helperText={touched.password && errors.password}
          />

          <Field
            as={TextField}
            name="confirm_password"
            label="Confirm Password *"
            placeholder="Enter your confirm password"
            fullWidth
            margin="normal"
            onChange={handleChange}
            error={touched.confirm_password && Boolean(errors.confirm_password)}
            helperText={touched.confirm_password && errors.confirm_password}
          />

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ mt: 2, bgcolor: 'green', '&:hover': { bgcolor: 'darkgreen' } }}
            onClick={(e) => onsubmit(e)}
            disabled={!isValid || !dirty || isSubmitting}
          >
            Change Password
          </Button>

          {alertMessage.message !== '' && (
            <Box sx={{ my: 1 }}>
              <CustomAlert type={alertMessage.type}>{alertMessage.message}</CustomAlert>
            </Box>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default UserChangePasswordForm;
